import axios, { AxiosResponse, AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios'
import { ApiGeneralSinglePayload } from '@/tcw/api/types'
import * as ApiPath from '@/tcw/constant/ApiPath'
import {
  TCWApiError,
  TCWUnexpectedError,
  TCWSessionError,
  TCWServerValidationError
} from '@/plugins/TCWBaseError'
import { ERR_CODE_UNEXPECTED, ERR_CODE_WEB_API_NOT_FOUND } from '@/tcw/constant/ErrorCode'

export interface IApiRequest {
  call: <T>(path: string, method: MethodType, params?: ApiGeneralSinglePayload) => Promise<T>
}

export class ApiAgent {
  private axios: AxiosInstance | undefined
  private options: AxiosRequestConfig = {}
  private static getDefaultOptions(token?: string): AxiosRequestConfig {
    return {
      headers: {
        'Content-Type': 'application/json',
        'X-XSRF-TOKEN': token
      },
      responseType: 'json',
      withCredentials: true
    }
  }

  constructor(baseURL: string, options = {}) {
    this.options = Object.assign({}, options)
    this.options.baseURL = baseURL
    this.axios = axios.create(this.options)
  }

  public static create(baseURL: string, token?: string): ApiAgent {
    return new ApiAgent(baseURL, this.getDefaultOptions(token))
  }

  public async postWithMultipart<T>(path: string, params: FormData): Promise<T> {
    return await this.call<T>(path, 'POST', params, {
      headers: {
        'content-type': 'multipart/form-data'
      }
    })
  }

  public async callRaw<T>(
    path: string,
    method: MethodType,
    params?: ApiGeneralSinglePayload | FormData,
    options?: AxiosRequestConfig
  ): Promise<AxiosResponse<T>> {
    if (this.axios === undefined) {
      throw new TypeError('インスタンスが初期化されていません')
    }

    let response: AxiosResponse<T> | undefined
    try {
      console.info(`API Call: ${path} Method: ${method}`)
      console.debug('params: ', params)
      switch (method) {
        case 'GET':
          if (params) {
            path = ApiPath.buildQueryString(path, params as ApiGeneralSinglePayload)
          }
          response = await this.axios.get<T>(path, options)
          break
        case 'PUT':
          response = await this.axios.put<T>(path, params, options)
          break
        case 'POST':
          response = await this.axios.post<T>(path, params, options)
          break
        case 'DELETE':
          if (params) {
            path = ApiPath.buildQueryString(path, params as ApiGeneralSinglePayload)
          }
          response = await this.axios.delete<T>(path, options)
          break
      }

      console.info('received response')
      console.info(response)

      return response
    } catch (e) {
      // ステータスコード200以外はaxiosがエラーを吐く
      console.error(e)
      const axiosError = e as AxiosError
      if (axiosError.response) {
        const status = axiosError.response.status
        const statusText = axiosError.message
        const errorBody = axiosError.response.data
        console.log('APIサーバリクエストに失敗: ' + statusText)
        switch (status) {
          case 400: {
            if (errorBody?.errorCode === 'ERR003') {
              // 入力チェックエラー
              throw new TCWServerValidationError(axiosError)
            } else if (errorBody?.errorCode === 'ERR004') {
              // データチェックエラー
              throw new TCWServerValidationError(axiosError)
            } else {
              throw new TCWApiError(ERR_CODE_UNEXPECTED, axiosError)
            }
          }
          case 401:
            throw new TCWSessionError(errorBody?.message, axiosError)
          case 404:
            throw new TCWApiError(ERR_CODE_WEB_API_NOT_FOUND, axiosError)
          default:
            throw new TCWApiError(ERR_CODE_UNEXPECTED, axiosError)
        }
      } else if (e instanceof Error) {
        // axios以外のエラーは予期せぬエラー
        throw new TCWUnexpectedError(e.message, e)
      } else {
        throw new TCWUnexpectedError('予期せぬエラーが発生しました')
      }
    }
  }

  public async call<T>(
    path: string,
    method: MethodType,
    params?: ApiGeneralSinglePayload | FormData,
    options?: AxiosRequestConfig
  ): Promise<T> {
    const response = await this.callRaw<T>(path, method, params, options)
    return response.data
  }
}

export type MethodType = 'GET' | 'POST' | 'PUT' | 'DELETE'

export const getCookies = (): { [key: string]: string } => {
  const cookies: { [key: string]: string } = {}

  // トークン取り出し
  if (!document.cookie) {
    return cookies
  }

  document.cookie.split(';').reduce((map: { [key: string]: string }, cookie: string) => {
    const ary = cookie.split('=')
    if (ary.length === 2) {
      map[String(ary[0]).trim()] = String(ary[1]).trim()
    }
    return map
  }, cookies)

  return cookies
}

export const getCookie = (name: string): string => {
  const cookies: { [key: string]: string } = getCookies()
  return cookies[name]
}
